import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, User, FileText, ChevronRight } from 'lucide-react';
import { SearchResult } from '../../utils/search/types';
import { useLanguage } from '../../contexts/LanguageContext';

interface SearchResultsProps {
  results: SearchResult[];
  onSelect: () => void;
}

const SearchResults = ({ results, onSelect }: SearchResultsProps) => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const getIcon = (type: SearchResult['type']) => {
    switch (type) {
      case 'job':
        return <Briefcase className="w-4 h-4 text-blue-500" />;
      case 'candidate':
        return <User className="w-4 h-4 text-purple-500" />;
      case 'reference':
        return <FileText className="w-4 h-4 text-green-500" />;
    }
  };

  const getLabel = (type: SearchResult['type']) => {
    switch (type) {
      case 'job':
        return t('search.resultTypes.job');
      case 'candidate':
        return t('search.resultTypes.candidate');
      case 'reference':
        return t('search.resultTypes.reference');
    }
  };

  const handleSelect = (result: SearchResult) => {
    navigate(result.url);
    onSelect();
  };

  // Group results by type for better organization
  const groupedResults = results.reduce<Record<string, SearchResult[]>>(
    (acc, result) => {
      if (!acc[result.type]) {
        acc[result.type] = [];
      }
      acc[result.type].push(result);
      return acc;
    },
    {}
  );

  // Order of display: jobs, candidates, references
  const displayOrder: SearchResult['type'][] = ['job', 'candidate', 'reference'];

  return (
    <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border py-2 max-h-[400px] overflow-y-auto z-50">
      {results.length > 0 ? (
        <>
          {displayOrder.map((type) => 
            groupedResults[type] ? (
              <div key={type} className="py-1">
                <div className="px-4 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">
                  {getLabel(type)}
                </div>
                {groupedResults[type].map((result) => (
                  <button
                    key={result.id}
                    onClick={() => handleSelect(result)}
                    className="w-full px-4 py-2 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      {getIcon(result.type)}
                      <div>
                        <div className="font-medium text-gray-900">{result.title}</div>
                        <div className="text-sm text-gray-500">{result.subtitle}</div>
                      </div>
                    </div>
                    <div className="flex items-center text-blue-500 text-sm">
                      <span>{t('search.seeDetails')}</span>
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </div>
                  </button>
                ))}
              </div>
            ) : null
          )}
        </>
      ) : (
        <div className="px-4 py-3 text-sm text-gray-500 text-center">
          {t('search.noResults')}
        </div>
      )}
    </div>
  );
};

export default SearchResults;